import Image from "next/image";
import { backgroundGradientStyles } from "@/data/styles.data";
import { ReactNode } from "react";

interface StickyCardProps {
  className?: string;
  classNameForInnerParent?: string;
  classNameForDot?: string;
  title: string;
  description: string;
  icon?: ReactNode;
  /**
   * Optional root-relative path (e.g. `/assets/services/banner-services/x.png`)
   * to a square illustration rendered as a subtle floating decoration in the
   * card's bottom-right corner. Purely visual; the wrapper is `aria-hidden`
   * and uses `pointer-events-none` so it never interferes with hit-testing.
   */
  backgroundImage?: string;
}

/**
 * Sticky service card used in the home banner.
 *
 *   ✦ Server-friendly. No `"use client"`, no hooks, no DOM APIs.
 *   ✦ The animated purple/white outer ring comes from the parent
 *     `<section>`'s CSS-only `waveSlide` keyframe (see `styles.data.ts`).
 *   ✦ Card dimensions, padding, rotation, and hover scale are unchanged.
 *
 * Visual stack (back → front) on a clean white surface:
 *   1. White card surface with a faint purple accent border + soft shadow.
 *   2. Soft purple ambient glow tucked into the bottom-right corner.
 *   3. Decorative PNG illustration floating in the bottom-right (~50%).
 *   4. Content layer (accent line, title, description, icon) on `relative z-10`.
 */
const StickyCard = ({
  className,
  classNameForInnerParent,
  classNameForDot,
  title,
  description,
  icon,
  backgroundImage,
}: StickyCardProps) => {
  return (
    <section
      style={backgroundGradientStyles}
      className={`h-full cursor-pointer hover:scale-105 duration-200 bg-gray-500 ${className} rounded-lg`}
    >
      <section
        style={{
          border: "1px solid rgba(139,92,246,0.18)",
          boxShadow:
            "0 18px 40px -22px rgba(132,109,255,0.22), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}
        className={`relative flex flex-col w-[calc(100%-6px)] h-[calc(100%-6px)] overflow-hidden p-6 rounded-lg text-black bg-white ${classNameForInnerParent}`}
      >
        {/* Ambient radial glow tucked into the bottom-right corner. Adds
            warmth/depth on the white surface without competing with text. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -bottom-10 size-48 rounded-full bg-primary/15 blur-3xl"
          style={{ zIndex: 0 }}
        />

        {/* Decorative PNG illustration — bottom-right corner, partial bleed.
            Sits behind the content via z-index 0, never interactable. */}
        {backgroundImage && (
          <div
            aria-hidden
            className="pointer-events-none absolute"
            style={{
              right: "-8px",
              bottom: "-8px",
              width: "65%",
              height: "65%",
              opacity: 0.4,
              zIndex: 0,
            }}
          >
            <Image
              src={backgroundImage}
              alt=""
              fill
              sizes="(min-width: 1280px) 12vw, 24vw"
              className="object-contain object-right-bottom"
            />
          </div>
        )}

        {/* ===== Content layer ===== */}
        <div className="relative z-10 flex h-full flex-col">
          {/* Small gradient accent line near the title — modern flourish that
              works on white because it carries the brand purple. */}
          <span
            aria-hidden
            className="mb-3 inline-block h-[3px] w-9 rounded-full bg-gradient-to-r from-primary via-primary/70 to-primary/0"
          />

          {/* Title + description use a per-line highlight backdrop. The
              `box-decoration-clone` makes the rounded white background
              wrap around each rendered line of text rather than the full
              <p> box, so the illustration stays visible everywhere except
              directly behind the actual characters. */}
          <article className="space-y-[6px]">
            <p className="!text-base sm:!text-lg lg:!text-xl">
              <strong className="box-decoration-clone rounded-sm bg-white/60 px-1.5 py-0.5 font-semibold leading-relaxed text-black">
                {title}
              </strong>
            </p>
            <p className="text-sm leading-relaxed sm:!text-sm 2xl:!text-base">
              <span className="box-decoration-clone rounded-sm bg-white/50 px-1.5 py-0.5 text-black/75">
                {description}
              </span>
            </p>
          </article>

          {/* Bottom-left: icon. `w-fit` keeps it from stretching into the
              decorative corner area. */}
          <section className="flex flex-1 flex-col justify-end">
            <div className="w-fit">{icon}</div>
          </section>
        </div>

        {/* Corner indicator dot — back to a dark dot now that the card is
            white again. */}
        <div
          className={`absolute z-10 rounded-full size-2 bg-black ${classNameForDot}`}
        />
      </section>
    </section>
  );
};

export default StickyCard;
